import { useCallback, useEffect, useState } from 'react';

import { createAdminApi, type AdminIdentity, type AdminRole } from '@/admin/adminApi';
import { useHttpClient } from '@/app/providers';

import { isForbidden, messageOf } from './adminErrors';

/**
 * Role d'administration du compte connecte, lu sur `GET /admin/me`.
 *
 * Trois issues, jamais deux — et la distinction porte tout le comportement des
 * ecrans :
 *
 * - **`granted`** : le compte a au moins le role `support`. Ce qu'il peut FAIRE
 *   depend ensuite de `roleSatisfies(role, 'admin')`, verifie ecran par ecran.
 * - **`denied`** : le serveur a repondu **403**. C'est l'etat de tout compte
 *   parent (ADR 0032 : `admin_role` nul par defaut), et ce n'est pas une panne.
 *   L'ecran l'explique et ne tente aucune autre requete — en tenter une
 *   afficherait une seconde erreur pour la meme cause.
 * - **`error`** : API injoignable, 500, session perdue. La seule issue ou un
 *   « Réessayer » a un sens.
 *
 * Le role n'est **jamais deduit** d'autre chose (ADR 0032, point 7) : ni du
 * jeton, qui est opaque, ni d'un reglage local. Cette route est la seule source.
 *
 * Nota : l'API ne rend pas `adminRole: null` sur `200` — la garde refuse avant,
 * en 403. Le cas est neanmoins traite comme `denied`, parce que le type
 * `AdminIdentity` l'autorise et qu'un ecran qui ferait confiance a un `null`
 * pour autoriser quoi que ce soit serait un ecran a corriger.
 */
export type AdminAccess =
  | { readonly kind: 'loading' }
  | { readonly kind: 'granted'; readonly identity: AdminIdentity; readonly role: AdminRole }
  | { readonly kind: 'denied' }
  | { readonly kind: 'error'; readonly message: string };

/** Issue d'une tentative, rangee avec le numero de tentative qui l'a produite. */
interface Settled {
  readonly attempt: number;
  readonly access: Exclude<AdminAccess, { kind: 'loading' }>;
}

export interface AdminAccessState {
  readonly access: AdminAccess;
  readonly reload: () => void;
}

export const useAdminAccess = (): AdminAccessState => {
  const http = useHttpClient();
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<Settled | null>(null);

  useEffect(() => {
    // Le drapeau evite d'ecrire l'etat d'une reponse dont plus personne ne veut
    // — un « Réessayer » clique deux fois, un demontage pendant l'appel.
    let cancelled = false;

    // `try/catch` autour d'un `await`, et non un `.catch()` sur la promesse :
    // une implementation du client qui echouerait de facon SYNCHRONE — un
    // double de test, une URL invalide — passerait a cote d'un `.catch()` et
    // remonterait en erreur de rendu. La forme `async` rattrape les deux.
    const load = async (): Promise<void> => {
      try {
        const identity = await createAdminApi(http).identity();

        if (cancelled) {
          return;
        }

        setSettled({
          attempt,
          access:
            identity.adminRole === null
              ? { kind: 'denied' }
              : { kind: 'granted', identity, role: identity.adminRole },
        });
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        setSettled({
          attempt,
          access: isForbidden(error) ? { kind: 'denied' } : { kind: 'error', message: messageOf(error) },
        });
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [http, attempt]);

  const reload = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  // L'attente est **deduite**, jamais ecrite : tant que l'issue rangee ne porte
  // pas le numero de la tentative en cours, c'est qu'on attend encore. Poser un
  // `setState('loading')` dans l'effet produirait un rendu de plus a chaque
  // passage, et React le deconseille explicitement.
  const access: AdminAccess = settled !== null && settled.attempt === attempt ? settled.access : { kind: 'loading' };

  return { access, reload };
};
